// src/pages/teacher/exams/TeacherQuestionBank.js
import React, { useEffect, useMemo, useState, useDeferredValue } from "react";
import {
  Card, Row, Col, Button, Badge, Form, InputGroup, Spinner, Alert, Modal,Table
} from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import {
  collection, doc, onSnapshot, query, orderBy, limit,
  getDocs, where, writeBatch, serverTimestamp,updateDoc
} from "firebase/firestore";
import { db } from "../../../services/firebase";
import useSchool from "../../../hooks/useSchool";
import { useAuth } from "../../../context/AuthContext";

/* ---------------- constants / helpers ---------------- */
const TYPES = [
  "mcq","multi_correct","true_false","descriptive","match",
  "assertion_reason","statement_based","case_based","mcq_descriptive"
];
const DIFFS = ["Easy","Medium","Hard"];
const lower = (s) => String(s || "").trim().toLowerCase();

const stripHtml = (html = "") =>
  String(html || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

// facet helpers (robust to varied sources)
const getClassLabel   = (q) => q.className || q.class || q.grade || q.classId || "";
const getChapterLabel = (q) => q.chapter || q.chapterName || q.chapterNo || "";
const getTopicLabel   = (q) => q.topic || "";

/* ----- normalize for fingerprint ----- */
const decodeHtml = (s = "") => {
  const el = document.createElement("textarea");
  el.innerHTML = s;
  return el.value;
};
const normalizeHtml = (html = "") =>
  decodeHtml(String(html || ""))
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;|\u00a0/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

// FNV-1a 32-bit
const hash32 = (s = "") => {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, "0");
};

const buildFingerprint = (q) => {
  const type = q.questionType || "mcq";
  const stem =
    type === "case_based"
      ? normalizeHtml(q.passageHtml)
      : normalizeHtml(q.questionHtml);

  let optionsStr = "";
  if (type === "match") {
    if (Array.isArray(q.matchOptions) && q.matchOptions.length) {
      optionsStr = q.matchOptions.map(arr => (arr || []).join(",")).join("|");
    } else {
      const mp = (q.matchPairs || [])
        .map(r => `${normalizeHtml(r.leftHtml)}::${normalizeHtml(r.rightHtml)}`)
        .join("|");
      optionsStr = mp;
    }
  } else if (type === "case_based") {
    optionsStr = (q.caseSubQs || [])
      .map(sq => {
        const sqOpts = (sq.options || []).map(o => normalizeHtml(o.value || "")).join(",");
        return `${normalizeHtml(sq.questionHtml)}::${sqOpts}::${sq.correctKey || ""}`;
      })
      .join("|");
  } else {
    optionsStr = (q.options || []).map(o => normalizeHtml(o?.value || "")).join("|");
  }

  const stmts = (q.statements || [])
    .map(s => `${normalizeHtml(s.html)}:${s.isTrue ? "1" : "0"}`)
    .join("|");

  const parts = [
    q.subject || "",
    getChapterLabel(q) || "",
    getTopicLabel(q) || "",
    type,
    stem,
    optionsStr,
    String(q.matchPairCount || 0),
    stmts,
    q.trueFalseAnswer || "",
    q.arAnswer || "",
    normalizeHtml(q.descPartHtml || ""),
    String(q.correctOptionIndex ?? "")
  ];
  return parts.join("||");
};

// Deterministic id inside a template + section (lets you add same Q to multiple sections)
const stableDocId = (templateId, fingerprint, sectionKey = "") =>
  `tpl_${templateId}_${hash32(`${fingerprint}__${sectionKey}`)}`;

/* ---------- Skeleton ---------- */
function CardSkeleton() {
  return (
    <div className="qb-skel">
      <div className="line w-50" />
      <div className="line w-35 mt-2" />
      <div className="line w-90 mt-3" />
      <div className="line w-80 mt-1" />
      <div className="line w-70 mt-1" />
    </div>
  );
}

/* ===================================================== */
export default function TeacherQuestionBank() {
  const { id: templateId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { schoolId, loading: schoolLoading } = useSchool();

  /* ---------- template ---------- */
  const [tpl, setTpl] = useState(null);
  const [tplLoading, setTplLoading] = useState(true);

  /* ---------- bank (shared + school) ---------- */
  const [loading, setLoading] = useState(true);
  const [sharedBank, setSharedBank] = useState([]); // /questionbank
  const [localBank, setLocalBank] = useState([]);   // /schools/{sid}/questionbank

  /* ---------- filters ---------- */
  const [qText, setQText] = useState("");
  const dqText = useDeferredValue(qText);

  const [fClass, setFClass]     = useState("");
  const [fSubject, setFSubject] = useState("");
  const [fChapter, setFChapter] = useState("");
  const [fTopic, setFTopic]     = useState("");
  const [fType, setFType]       = useState("all");
  const [fDiff, setFDiff]       = useState("all");

  /* ---------- selection ---------- */
  const [selected, setSelected] = useState(new Map()); // id -> doc

  /* ---------- mapping & overrides (to template) ---------- */
  const [mapSubjectId, setMapSubjectId] = useState(""); // chosen subject row id/name from template
  const [mapSections, setMapSections]   = useState([]); // chosen section keys
  const [ovMarks, setOvMarks]           = useState(""); // optional overrides
  const [ovNeg, setOvNeg]               = useState("");
const [statusSaving, setStatusSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [preview, setPreview]     = useState(null);

  const [paperOpen, setPaperOpen] = useState(false);

const setTemplateStatus = async (nextStatus) => {
   if (!schoolId || !templateId) return;
   try {
     setStatusSaving(true);
     await updateDoc(
       doc(db, "schools", schoolId, "examTemplates", templateId),
       { status: nextStatus, updatedAt: serverTimestamp() }
     );
   } catch (e) {
     alert(e?.message || "Failed to update status.");
   } finally {
     setStatusSaving(false);
   }
};
  /* ---------- load template ---------- */
  useEffect(() => {
    if (!schoolId || !templateId) return;
    setTplLoading(true);
    const ref = doc(db, "schools", schoolId, "examTemplates", templateId);
    const unsub = onSnapshot(
      ref,
      (snap) => { setTpl(snap.exists() ? ({ id: snap.id, ...(snap.data()||{}) }) : null); setTplLoading(false); },
      ()     => { setTpl(null); setTplLoading(false); }
    );
    return () => unsub();
  }, [schoolId, templateId]);

  // template subject options (for mapping panel)
  const tplSubjectOptions = useMemo(() => {
    const arr = (tpl?.subjectWise || []).map((s) => ({
      value: s.subjectId || s.subjectName || "",
      label: s.subjectName || s.subjectId || "(Untitled)",
      _row: s
    }));
    return arr;
  }, [tpl?.subjectWise]);

  const currentSubjectRow = useMemo(() => {
    if (!mapSubjectId) return null;
    return (tpl?.subjectWise || []).find(
      (s) => (s.subjectId || s.subjectName || "") === mapSubjectId
    ) || null;
  }, [tpl?.subjectWise, mapSubjectId]);

  const sectionOptions = useMemo(() => {
    const secs = currentSubjectRow?.sections || [];
    return secs.map((s) => ({ value: s.key || s.label || "A", label: s.label || s.key || "A" }));
  }, [currentSubjectRow]);
const sectionDefaults = useMemo(() => {
  const map = new Map();
  (currentSubjectRow?.sections || []).forEach((s) => {
    const key = s.key || s.label || "A";
    const marks = Number.isFinite(+s.marksPerCorrect) ? +s.marksPerCorrect : 1;
    const negative = Number.isFinite(+s.negativeMarks) ? +s.negativeMarks : 0;
    map.set(key, { marks, negative, label: s.label || key });
  });
  return map;
}, [currentSubjectRow]);

  /* ---------- load shared & local banks ---------- */
  useEffect(() => {
    setLoading(true);
    const ref = collection(db, "questionbank");
    const qy = query(ref, orderBy("updatedAt", "desc"), limit(2000));
    const unsub = onSnapshot(
      qy,
      (snap) => {
        setSharedBank(snap.docs.map(d => ({ id: d.id, _src: "shared", ...(d.data() || {}) })));
        setLoading(false);
      },
      () => setLoading(false)
    );
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!schoolId) return;
    const ref = collection(db, "schools", schoolId, "questionbank");
    const qy = query(ref, orderBy("updatedAt", "desc"), limit(2000));
    const unsub = onSnapshot(
      qy,
      (snap) => setLocalBank(snap.docs.map(d => ({ id: d.id, _src: "local", ...(d.data() || {}) }))),
      () => setLocalBank([]) // ignore if collection missing or denied
    );
    return () => unsub();
  }, [schoolId]);


/* ---------- helpers for preview paper ---------- */
const optionLetter = (i) => String.fromCharCode(65 + i); // A, B, C ...

const toRoman = (num) => {
  const map = [[1000,"m"],[900,"cm"],[500,"d"],[400,"cd"],[100,"c"],[90,"xc"],[50,"l"],[40,"xl"],[10,"x"],[9,"ix"],[5,"v"],[4,"iv"],[1,"i"]];
  let res = "", n = Math.max(1, Math.floor(num));
  for (const [v,s] of map) while (n >= v) { res += s; n -= v; }
  return res;
};
const leftLabel = (idx) => {
  let n = idx + 1, s = "";
  while (n > 0) { n--; s = String.fromCharCode(97 + (n % 26)) + s; n = Math.floor(n / 26); }
  return s; // a, b, c...
};
const rightLabel = (idx) => toRoman(idx + 1);
const formatMatchOptionText = (arr = []) =>
  (!arr.length || arr.some(v => v == null))
    ? "—"
    : arr.map((rIdx, lIdx) => `(${leftLabel(lIdx)})-(${rightLabel(rIdx)})`).join(", ");

/** Render question WITHOUT revealing answers */
function QuestionBody({ q }) {
  const t = q.questionType || "mcq";

  const renderOptions = (opts = []) => (
    <ol className="mb-2" type="A">
      {opts.map((o, i) => (
        <li key={o.key ?? i}>
          <div dangerouslySetInnerHTML={{ __html: o?.value || "<em class='text-muted'>—</em>" }} />
        </li>
      ))}
    </ol>
  );

  return (
    <div className="small">
      {/* stem / passage */}
      {t !== "case_based" ? (
        <div className="mb-2" dangerouslySetInnerHTML={{ __html: q.questionHtml || "" }} />
      ) : (
        <>
          <div className="text-muted">Passage / Case</div>
          <div className="mb-2" dangerouslySetInnerHTML={{ __html: q.passageHtml || "" }} />
        </>
      )}

      {/* type specifics WITHOUT answers */}
      {["mcq","multi_correct","mcq_descriptive"].includes(t) && renderOptions(q.options || [])}

      {t === "mcq_descriptive" && q.descPartHtml && (
        <>
          <div className="text-muted">Descriptive Part</div>
          <div className="mb-2" dangerouslySetInnerHTML={{ __html: q.descPartHtml }} />
        </>
      )}

      {t === "true_false" && (
        <div className="mb-2"><em className="text-muted">True / False</em></div>
      )}

      {t === "match" && (
        <>
          {Array.isArray(q.matchPairs) && q.matchPairs.length ? (
            <Table size="sm" bordered className="mb-2">
              <thead><tr><th>List-I</th><th>List-II</th></tr></thead>
              <tbody>
                {q.matchPairs.filter(r => r.leftHtml || r.rightHtml).map((r, i) => (
                  <tr key={r.id || i}>
                    <td dangerouslySetInnerHTML={{ __html: r.leftHtml || "" }} />
                    <td dangerouslySetInnerHTML={{ __html: r.rightHtml || "" }} />
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div className="mb-2">
              <div className="text-muted">Pairs: {q.matchPairCount || "-"}</div>
              {Array.isArray(q.matchOptions) && q.matchOptions.length > 0 && (
                <div className="mt-1">
                  {q.matchOptions.map((arr, i) => (
                    <div key={i} className="mb-1">
                      <Badge bg="secondary" className="me-2">Option {i+1}</Badge>
                      <span className="text-muted">{formatMatchOptionText(arr)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {t === "assertion_reason" && (
        <>
          <div><strong>Assertion:</strong></div>
          <div className="mb-2" dangerouslySetInnerHTML={{ __html: q.assertionHtml || "" }} />
          <div><strong>Reason:</strong></div>
          <div className="mb-2" dangerouslySetInnerHTML={{ __html: q.reasonHtml || "" }} />
        </>
      )}

      {t === "statement_based" && (
        <div className="mb-2">
          {(q.statements || []).map((s, i) => (
            <div key={s.id || i} className="mb-1">
              <strong>{s.key || String.fromCharCode(65+i)}.</strong>{" "}
              <span dangerouslySetInnerHTML={{ __html: s.html || "" }} />
            </div>
          ))}
        </div>
      )}

      {t === "case_based" && (
        <div className="mb-2">
          {(q.caseSubQs || []).map((sq, i) => (
            <div className="mb-2" key={sq.id || i}>
              <div><small className="text-muted">Sub-question {i+1}</small></div>
              <div dangerouslySetInnerHTML={{ __html: sq.questionHtml || "" }} />
              <ol className="mt-1" type="A">
                {(sq.options || []).map((o, j) => (
                  <li key={o.key ?? j}>
                    <div dangerouslySetInnerHTML={{ __html: o.value || "<em class='text-muted'>—</em>" }} />
                  </li>
                ))}
              </ol>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Build a compact answer string for the key */
function buildAnswerLabel(q) {
  const t = q.questionType || "mcq";

  if (t === "true_false") {
    return q.trueFalseAnswer || "True";
  }

  if (t === "assertion_reason") {
    return q.arAnswer || "A";
  }

  if (t === "statement_based") {
    const truths = (q.statements || [])
      .map((s, i) => (s.isTrue ? (s.key || String.fromCharCode(65+i)) : null))
      .filter(Boolean);
    return truths.length ? truths.join(", ") : "—";
  }

  if (t === "match") {
    if (Array.isArray(q.matchOptions) && typeof q.correctOptionIndex === "number") {
      const idx = q.correctOptionIndex;
      const text = formatMatchOptionText(q.matchOptions[idx] || []);
      return `Option ${idx + 1}${text ? ` — ${text}` : ""}`;
    }
    // legacy pairs have no explicit mapping answer
    return "See pairs";
  }

  if (t === "case_based") {
    const parts = (q.caseSubQs || []).map((sq, i) => {
      const letter = (sq.options || []).findIndex(o => o.key && o.key === sq.correctKey);
      return `Q${i+1}: ${letter >= 0 ? optionLetter(letter) : (sq.correctKey || "—")}`;
    });
    return parts.join(" | ");
  }

  // mcq / multi_correct / mcq_descriptive
  const flags = (q.options || []).map((o, i) => {
    const isCorrect =
      !!o?.isCorrect ||
      (typeof q.correctOptionIndex === "number" && q.correctOptionIndex === i) ||
      (q.correctKey && o?.key && q.correctKey === o.key);
    return isCorrect ? optionLetter(i) : null;
  }).filter(Boolean);

  if (flags.length) return flags.join(", ");
  return "—";
}

/* ---------- Preview details renderer ---------- */
function QuestionDetails({ q }) {
  const type = q.questionType || "mcq";

  const renderOptions = (opts = []) => (
    <ol className="mb-2" type="A">
      {opts.map((o, i) => {
        const isCorrect =
          !!o?.isCorrect ||
          (typeof q.correctOptionIndex === "number" && q.correctOptionIndex === i) ||
          (q.correctKey && o?.key && q.correctKey === o.key);
        return (
          <li key={o.key ?? i}>
            <div dangerouslySetInnerHTML={{ __html: o?.value || "<em class='text-muted'>—</em>" }} />
            {isCorrect && <Badge bg="success" className="ms-2">Correct</Badge>}
          </li>
        );
      })}
    </ol>
  );

  return (
    <div className="small">
      {/* Stem / Passage */}
      {type !== "case_based" ? (
        <div className="mb-2" dangerouslySetInnerHTML={{ __html: q.questionHtml || "" }} />
      ) : (
        <>
          <div className="text-muted">Passage / Case</div>
          <div className="mb-2" dangerouslySetInnerHTML={{ __html: q.passageHtml || "" }} />
        </>
      )}

      {/* Type-specific details */}
      {["mcq","multi_correct","mcq_descriptive"].includes(type) && renderOptions(q.options || [])}

      {type === "mcq_descriptive" && q.descPartHtml && (
        <>
          <div className="text-muted">Descriptive Part</div>
          <div className="mb-2" dangerouslySetInnerHTML={{ __html: q.descPartHtml }} />
        </>
      )}

      {type === "true_false" && (
        <div className="mb-2"><Badge bg="info">Answer: {q.trueFalseAnswer || "True"}</Badge></div>
      )}

      {type === "match" && (
        <>
          {Array.isArray(q.matchPairs) && q.matchPairs.length ? (
            <Table size="sm" bordered className="mb-2">
              <thead><tr><th>List-I</th><th>List-II</th></tr></thead>
              <tbody>
                {q.matchPairs.filter(r => r.leftHtml || r.rightHtml).map((r, i) => (
                  <tr key={r.id || i}>
                    <td dangerouslySetInnerHTML={{ __html: r.leftHtml || "" }} />
                    <td dangerouslySetInnerHTML={{ __html: r.rightHtml || "" }} />
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div className="mb-2">
              <div className="text-muted">Pairs: {q.matchPairCount || "-"}</div>
              {Array.isArray(q.matchOptions) && q.matchOptions.length > 0 && (
                <div className="mt-1">
                  {q.matchOptions.map((arr, i) => (
                    <div key={i} className="mb-1">
                      <Badge bg={q.correctOptionIndex === i ? "success" : "secondary"} className="me-2">
                        Option {i+1}
                      </Badge>
                      <span className="text-muted">{formatMatchOptionText(arr)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {type === "assertion_reason" && (
        <>
          <div><strong>Assertion:</strong></div>
          <div className="mb-2" dangerouslySetInnerHTML={{ __html: q.assertionHtml || "" }} />
          <div><strong>Reason:</strong></div>
          <div className="mb-2" dangerouslySetInnerHTML={{ __html: q.reasonHtml || "" }} />
          <div><Badge bg="info">Answer: {q.arAnswer || "A"}</Badge></div>
        </>
      )}

      {type === "statement_based" && (
        <div className="mb-2">
          {(q.statements || []).map((s, i) => (
            <div key={s.id || i} className="mb-1">
              <strong>{s.key || String.fromCharCode(65+i)}.</strong>{" "}
              <span dangerouslySetInnerHTML={{ __html: s.html || "" }} />
              {s.isTrue && <Badge bg="success" className="ms-2">True</Badge>}
            </div>
          ))}
        </div>
      )}

      {type === "case_based" && (
        <div className="mb-2">
          {(q.caseSubQs || []).map((sq, i) => (
            <div className="mb-2" key={sq.id || i}>
              <div><small className="text-muted">Sub-question {i+1}</small></div>
              <div dangerouslySetInnerHTML={{ __html: sq.questionHtml || "" }} />
              <ol className="mt-1" type="A">
                {(sq.options || []).map((o, j) => (
                  <li key={o.key ?? j}>
                    <div dangerouslySetInnerHTML={{ __html: o.value || "<em class='text-muted'>—</em>" }} />
                    {sq.correctKey === o.key && <Badge bg="success" className="ms-2">Correct</Badge>}
                  </li>
                ))}
              </ol>
            </div>
          ))}
        </div>
      )}

      {/* Explanation */}
      {q.explanationHtml && (
        <>
          <div className="text-muted">Explanation</div>
          <div className="text-muted" dangerouslySetInnerHTML={{ __html: q.explanationHtml }} />
        </>
      )}
    </div>
  );
}

  /* ---------- facets (built from all loaded items) ---------- */
  const facetBase = useMemo(() => [...sharedBank, ...localBank], [sharedBank, localBank]);

  const classOptions = useMemo(() => {
    const s = new Set(facetBase.map(getClassLabel).filter(Boolean));
    return Array.from(s).sort();
  }, [facetBase]);

  const subjectOptions = useMemo(() => {
    const s = new Set(facetBase.map(q => q.subject).filter(Boolean));
    return Array.from(s).sort();
  }, [facetBase]);

  const chapterOptions = useMemo(() => {
    const s = new Set(facetBase.map(getChapterLabel).filter(Boolean));
    return Array.from(s).sort((a,b) => String(a).localeCompare(String(b), undefined, { numeric: true }));
  }, [facetBase]);

  const topicOptions = useMemo(() => {
    const s = new Set(facetBase.map(getTopicLabel).filter(Boolean));
    return Array.from(s).sort();
  }, [facetBase]);

  /* ---------- cascaded facet options (depend on current selections) ---------- */
const filteredSubjectOptions = useMemo(() => {
  const s = new Set();
  facetBase.forEach(q => {
    if (fClass && getClassLabel(q) !== fClass) return;
    if (q.subject) s.add(q.subject);
  });
  return Array.from(s).sort();
}, [facetBase, fClass]);

const filteredChapterOptions = useMemo(() => {
  const s = new Set();
  facetBase.forEach(q => {
    if (fClass && getClassLabel(q) !== fClass) return;
    if (fSubject && (q.subject || "") !== fSubject) return;
    const ch = getChapterLabel(q);
    if (ch) s.add(ch);
  });
  return Array.from(s).sort((a,b)=>String(a).localeCompare(String(b), undefined, {numeric:true}));
}, [facetBase, fClass, fSubject]);

const filteredTopicOptions = useMemo(() => {
  const s = new Set();
  facetBase.forEach(q => {
    if (fClass && getClassLabel(q) !== fClass) return;
    if (fSubject && (q.subject || "") !== fSubject) return;
    if (fChapter && getChapterLabel(q) !== fChapter) return;
    const tp = getTopicLabel(q);
    if (tp) s.add(tp);
  });
  return Array.from(s).sort();
}, [facetBase, fClass, fSubject, fChapter]);

  /* ---------- merge + de-dup + apply filters ---------- */
  const allBank = useMemo(() => {
    const combined = facetBase;

    const needle = lower(dqText);
    const pass = (q) => {
      if (fClass   && getClassLabel(q)   !== fClass)   return false;
      if (fSubject && (q.subject || "")  !== fSubject) return false;
      if (fChapter && getChapterLabel(q) !== fChapter) return false;
      if (fTopic   && getTopicLabel(q)   !== fTopic)   return false;
      if (fType !== "all" && (q.questionType || "mcq") !== fType) return false;
      if (fDiff !== "all" && (q.difficulty || "") !== fDiff) return false;

      if (needle) {
        const plain = stripHtml(q.questionHtml || q.passageHtml || "");
        const hay = [
          getClassLabel(q),
          q.subject || "",
          getChapterLabel(q),
          getTopicLabel(q),
          q.questionType || "",
          q.difficulty || "",
          plain,
        ].join(" ").toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    };

    const seen = new Set();
    const out = [];
    for (const q of combined) {
      if (!pass(q)) continue;
      const fp = q.dedupeKey || buildFingerprint(q);
      if (seen.has(fp)) continue;
      seen.add(fp);
      out.push({ ...q, _fp: fp });
    }
    return out;
  }, [facetBase, dqText, fClass, fSubject, fChapter, fTopic, fType, fDiff]);

  /* ---------- auto-reset invalid selections ---------- */
useEffect(() => {
  if (fSubject && !filteredSubjectOptions.includes(fSubject)) setFSubject("");
}, [filteredSubjectOptions, fSubject]);

useEffect(() => {
  if (fChapter && !filteredChapterOptions.includes(fChapter)) setFChapter("");
}, [filteredChapterOptions, fChapter]);

useEffect(() => {
  if (fTopic && !filteredTopicOptions.includes(fTopic)) setFTopic("");
}, [filteredTopicOptions, fTopic]);

  /* ---------- selection helpers ---------- */
  const toggleOne = (row) => {
    setSelected((m) => {
      const next = new Map(m);
      if (next.has(row.id)) next.delete(row.id); else next.set(row.id, row);
      return next;
    });
  };
  const selectAll = () => {
    setSelected((m) => {
      const next = new Map(m);
      allBank.forEach((r) => next.set(r.id, r));
      return next;
    });
  };
  const clearAll = () => setSelected(new Map());

  /* ---------- import with per-section de-dupe ---------- */
  const canImport = useMemo(() => {
    return (!!templateId && !!schoolId && selected.size > 0 && !!mapSubjectId && mapSections.length > 0);
  }, [templateId, schoolId, selected.size, mapSubjectId, mapSections.length]);
function typeSpecificFields(q) {
  const t = q.questionType || "mcq";

  // Always keep explanation if present
  const base = { explanationHtml: q.explanationHtml || "" };

  if (t === "mcq") {
    return {
      ...base,
      options: Array.isArray(q.options) ? q.options : [],
      singleCorrect: true,
      correctOptionIndex: typeof q.correctOptionIndex === "number" ? q.correctOptionIndex : undefined,
      correctKey: q.correctKey || undefined,
    };
  }

  if (t === "multi_correct") {
    return {
      ...base,
      options: Array.isArray(q.options) ? q.options : [],
      singleCorrect: false,
    };
  }

  if (t === "true_false") {
    return {
      ...base,
      trueFalseAnswer: q.trueFalseAnswer || "True",
    };
  }

  if (t === "descriptive") {
    return { ...base };
  }

  if (t === "mcq_descriptive") {
    return {
      ...base,
      options: Array.isArray(q.options) ? q.options : [],
      singleCorrect: true,
      descPartHtml: q.descPartHtml || "",
      correctOptionIndex: typeof q.correctOptionIndex === "number" ? q.correctOptionIndex : undefined,
      correctKey: q.correctKey || undefined,
    };
  }

  if (t === "assertion_reason") {
    return {
      ...base,
      assertionHtml: q.assertionHtml || "",
      reasonHtml: q.reasonHtml || "",
      arAnswer: q.arAnswer || "A",
    };
  }

  if (t === "statement_based") {
    return {
      ...base,
      statements: Array.isArray(q.statements) ? q.statements : [],
    };
  }

  if (t === "match") {
    // support both “options mapping” and legacy “pairs”
    const out = {
      ...base,
      matchPairCount: q.matchPairCount || (Array.isArray(q.matchPairs) ? q.matchPairs.length : undefined),
    };
    if (Array.isArray(q.matchOptions) && q.matchOptions.length) {
      out.matchOptions = q.matchOptions;
      if (typeof q.correctOptionIndex === "number") out.correctOptionIndex = q.correctOptionIndex;
    }
    if (Array.isArray(q.matchPairs) && q.matchPairs.length) {
      out.matchPairs = q.matchPairs;
    }
    return out;
  }

  if (t === "case_based") {
    return {
      ...base,
      passageHtml: q.passageHtml || "",
      caseSubQs: Array.isArray(q.caseSubQs) ? q.caseSubQs : [],
    };
  }

  // Fallback
  return base;
}

  const importSelected = async () => {
    if (!canImport) return;
    setImporting(true);
    try {
      const qsRef = collection(db, "schools", schoolId, "questions");

      // Load existing questions of this template to avoid duplicates (per section)
      const existingSnap = await getDocs(query(qsRef, where("templateId", "==", templateId)));
      const existingPerSection = new Set();
      existingSnap.forEach((d) => {
        const data = d.data() || {};
        const baseFp = data.dedupeKey || buildFingerprint(data);
        const sk = data.sectionKey || "";
        existingPerSection.add(`${baseFp}__${sk}`);
      });

      const now = serverTimestamp();
      const subjectRow   = currentSubjectRow;
      const subjectLabel = subjectRow?.subjectName || subjectRow?.subjectId || "Subject";
      const sections     = mapSections.length ? mapSections : [""];

      let addedCount = 0;
      const batch = writeBatch(db);

for (const [, qn] of selected) {
  const baseFp = qn.dedupeKey || buildFingerprint(qn);

  for (const sk of sections) {
    const fpPerSection = `${baseFp}__${sk}`;
    if (existingPerSection.has(fpPerSection)) continue;

    // ---- Section-based defaults (subjectRow is the selected template subject) ----
    const secDef = sectionDefaults.get(sk) || {};
    const sourceMarks = (typeof qn.marks === "number") ? qn.marks : 1;
    const sourceNeg   = (typeof qn.negativeMarks === "number") ? qn.negativeMarks : 0;

    // Overrides win -> else section default -> else source -> else fallback
    const marks =
      ovMarks !== "" && Number.isFinite(Number(ovMarks))
        ? Number(ovMarks)
        : (Number.isFinite(secDef.marks) ? secDef.marks : sourceMarks);

    // If your template has an explicit gate for negatives, honor it; otherwise use value
    const negativeMarksRaw =
      ovNeg !== "" && Number.isFinite(Number(ovNeg))
        ? Number(ovNeg)
        : (Number.isFinite(secDef.negative) ? secDef.negative : sourceNeg);

    const negativeMarks =
      (tpl?.allowNegativeMarking === false) ? 0 : negativeMarksRaw;

    const docId = stableDocId(templateId, baseFp, sk);
    const ref   = doc(db, "schools", schoolId, "questions", docId);

    batch.set(ref, {
      templateId,
      subject: subjectLabel,
      questionType: qn.questionType || "mcq",

      // keep stem empty for case-based (passage lives in passageHtml)
      questionHtml: qn.questionType === "case_based" ? "" : (qn.questionHtml || ""),

      difficulty: DIFFS.includes(qn.difficulty) ? qn.difficulty : "Medium",
      marks,
      negativeMarks,

      subjectId: subjectRow?.subjectId || "",
      sectionKey: sk,
      sectionLabel: secDef.label || sk,

      chapter: getChapterLabel(qn) || "",
      topic: getTopicLabel(qn) || "",

      // include options/answers/passage/statements/etc.
      ...typeSpecificFields(qn),

      source: {
        bankId: qn.id || "",
        ownerSchoolId: qn.ownerSchoolId || "",
        importedBy: { uid: user?.uid || "", role: "teacher" }
      },

      dedupeKey: baseFp,
      createdAt: now,
      updatedAt: now,
    });

    existingPerSection.add(fpPerSection);
    addedCount++;
  }
}



      if (addedCount > 0) await batch.commit();

      clearAll();
      alert(`Imported ${addedCount} question${addedCount === 1 ? "" : "s"} into template.`);
    } catch (e) {
      alert(e?.message || "Failed to import.");
    } finally {
      setImporting(false);
    }
  };

  /* ---------- UI ---------- */
  if (schoolLoading || tplLoading) {
    return (
      <div className="d-flex align-items-center justify-content-center py-5">
        <Spinner />
      </div>
    );
  }
  if (!tpl) return <Alert variant="warning">Template not found.</Alert>;

  const tplReady = String(tpl.status || "draft").toLowerCase() === "ready";
  const subjectsEmpty = (tpl.subjectWise || []).length === 0;

  return (
    <div className="container-fluid p-0">
      {/* Header */}
      <div className="qb-hero mb-3">
        <div className="qb-hero-inner">
          <div>
            <div className="tdb-eyebrow">Teacher • Question Bank</div>
            <h5 className="m-0 fw-bold">{tpl.name || "Template"}</h5>
            <div className="text-muted small">
              {tpl.totalQuestions || 0} total planned · {tplReady ? "Template is Ready" : "Template is Draft"}
            </div>
          </div>
          <div className="d-flex gap-2">
            <Button variant="outline-secondary" onClick={() => window.location.reload()}>
              <i className="bi bi-arrow-repeat me-1" /> Refresh
            </Button>
            <Button variant="outline-dark" onClick={() => nav(-1)}>
              <i className="bi bi-arrow-left-short me-1" /> Back
            </Button>
          </div>
        </div>
      </div>

      {!tplReady && (
        <Alert variant="warning" className="mb-3">
          This template is <b>Draft</b>. You can import questions, then mark it <b>Ready</b> in the template editor.
        </Alert>
      )}
      {subjectsEmpty && (
        <Alert variant="danger" className="mb-3">
          No <b>Subject-wise Distribution</b> in this template. Add at least one subject/section first.
          <div className="d-flex align-items-center gap-2">
     <Badge bg={tplReady ? "success" : "secondary"}>{tplReady ? "Ready" : "Draft"}</Badge>
     <Button
       size="sm"
       variant={tplReady ? "outline-warning" : "success"}
       disabled={statusSaving}
       onClick={() => setTemplateStatus(tplReady ? "draft" : "ready")}
     >
       {statusSaving ? "Saving…" : (tplReady ? "Mark Draft" : "Mark Ready")}
     </Button>

   </div>
        </Alert>
      )}

      {/* Filters */}
      <Card className="border-0 shadow-sm mb-3">
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col md={2}>
              <Form.Label className="mb-1">Class</Form.Label>
              <Form.Select value={fClass} onChange={(e) => { setFClass(e.target.value); setFSubject(""); setFChapter(""); setFTopic(""); }}>
                <option value="">All</option>
                {classOptions.map((c) => <option key={c} value={c}>{c}</option>)}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label className="mb-1">Subject</Form.Label>
              <Form.Select value={fSubject} onChange={(e) => { setFSubject(e.target.value); setFChapter(""); setFTopic(""); }}>
                <option value="">All</option>
 {filteredSubjectOptions.map((s) => <option key={s} value={s}>{s}</option>)}              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label className="mb-1">Lesson/Chapter</Form.Label>
              <Form.Select value={fChapter} onChange={(e) => { setFChapter(e.target.value); setFTopic(""); }}>
                <option value="">All</option>
{filteredChapterOptions.map((c) => <option key={c} value={c}>{c}</option>)}              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label className="mb-1">Topic</Form.Label>
              <Form.Select value={fTopic} onChange={(e) => setFTopic(e.target.value)}>
                <option value="">All</option>
{filteredTopicOptions.map((t) => <option key={t} value={t}>{t}</option>)}              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label className="mb-1">Type</Form.Label>
              <Form.Select value={fType} onChange={(e) => setFType(e.target.value)}>
                <option value="all">All</option>
                {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label className="mb-1">Difficulty</Form.Label>
              <Form.Select value={fDiff} onChange={(e) => setFDiff(e.target.value)}>
                <option value="all">All</option>
                {DIFFS.map((d) => <option key={d} value={d}>{d}</option>)}
              </Form.Select>
            </Col>

            <Col md={6}>
              <Form.Label className="mb-1">Search</Form.Label>
              <InputGroup>
                <InputGroup.Text><i className="bi bi-search" /></InputGroup.Text>
                <Form.Control
                  placeholder="Search class / subject / chapter / topic / content…"
                  value={qText}
                  onChange={(e) => setQText(e.target.value)}
                />
                {qText && (
                  <Button
                    variant="outline-secondary"
                    onClick={() => setQText("")}
                  >
                    Clear
                  </Button>
                )}
              </InputGroup>
            </Col>
            <Col md={6} className="d-flex align-items-end">
              <div className="ms-auto d-flex gap-2">
                <Button size="sm" variant="outline-secondary" onClick={selectAll}>Select all</Button>
                <Button size="sm" variant="outline-secondary" onClick={clearAll}>Clear</Button>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Two-column layout: Bank (left) | Mapping panel (right) */}
      <Row className="g-3">
        <Col lg={8}>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-transparent d-flex justify-content-between align-items-center">
              <div className="fw-semibold">Questions</div>
              <Badge bg="light" text="dark" className="border">{loading ? "…" : allBank.length}</Badge>
            </Card.Header>
            <Card.Body className="p-2">
              {loading ? (
                <div className="qb-grid">
                  {Array.from({ length: 6 }).map((_, i) => <CardSkeleton key={i} />)}
                </div>
              ) : allBank.length === 0 ? (
                <div className="p-4 text-muted text-center">No questions match your filters.</div>
              ) : (
                <div className="qb-grid">
                  {allBank.map((r) => (
                    <div key={r.id} className={`qb-card ${selected.has(r.id) ? "is-selected" : ""}`}>
                      <div className="d-flex justify-content-between align-items-start gap-2">
                        <div className="d-flex flex-column">
                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            <Form.Check
                              type="checkbox"
                              id={`sel-${r.id}`}
                              checked={selected.has(r.id)}
                              onChange={() => toggleOne(r)}
                              aria-label="Select question"
                            />
                            {/* meta badges */}
                            {getClassLabel(r) && <Badge bg="light" text="dark" className="border">{getClassLabel(r)}</Badge>}
                            {r.subject && <Badge bg="light" text="dark" className="border">{r.subject}</Badge>}
                            {getChapterLabel(r) && <Badge bg="light" text="dark" className="border">Ch: {getChapterLabel(r)}</Badge>}
                            {getTopicLabel(r) && <Badge bg="light" text="dark" className="border">{getTopicLabel(r)}</Badge>}
                            <Badge bg="info">{r.questionType || "-"}</Badge>
                            <Badge bg={r.difficulty === "Hard" ? "danger" : r.difficulty === "Easy" ? "success" : "secondary"}>
                              {r.difficulty || "Medium"}
                            </Badge>
                          </div>
                          {/* Removed "Updated X days ago" per request */}
                        </div>
                        <Button size="sm" variant="outline-secondary" onClick={() => setPreview(r)}>
                          Preview
                        </Button>
                      </div>

                      <div className="qb-html mt-2 small" dangerouslySetInnerHTML={{ __html: r.questionHtml || "" }} />
                    </div>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

      {/* === Right column: Mapping & Import (updated) === */}
<Col lg={4}>
  <Card className="border-0 shadow-sm qb-sticky">
    <Card.Header className="bg-transparent">
      <div className="fw-semibold">Mapping &amp; Import</div>
    </Card.Header>
    <Card.Body>
      {/* Subject select */}
      <div className="mb-3">
        <Form.Label className="mb-1">Map to Subject (from template)</Form.Label>
        <Form.Select
          value={mapSubjectId}
          onChange={(e) => { setMapSubjectId(e.target.value); setMapSections([]); }}
        >
          <option value="">Select subject…</option>
          {(tplSubjectOptions || []).map((s) => (
            <option key={s.value || s.label} value={s.value || s.label}>
              {s.label}
            </option>
          ))}
        </Form.Select>
        <Form.Text muted>Required for import.</Form.Text>
      </div>

      {/* Sections picker (now shows per-section marks/negative defaults) */}
      <div className="mb-3">
        <Form.Label className="mb-1 d-flex justify-content-between">
          <span>Sections (from subject)</span>
          <span className="small text-muted">{mapSections.length} selected</span>
        </Form.Label>

        <div className="d-flex flex-wrap gap-2">
          {sectionOptions.length === 0 ? (
            <span className="text-muted small">Pick a subject first</span>
          ) : sectionOptions.map((opt) => {
              const def = sectionDefaults.get(opt.value) || {};
              const tag =
                (Number.isFinite(def.marks) ? `+${def.marks}` : "+—") +
                " / " +
                (Number.isFinite(def.negative) ? `−${def.negative}` : "−—");
              const isSel = mapSections.includes(opt.value);
              return (
                <Badge
                  as="button"
                  key={opt.value}
                  bg={isSel ? "primary" : "light"}
                  className="border selectable-badge"
                  onClick={() => {
                    setMapSections((lst) =>
                      lst.includes(opt.value)
                        ? lst.filter((x) => x !== opt.value)
                        : [...lst, opt.value]
                    );
                  }}
                  title={`Section defaults • Marks: ${Number.isFinite(def.marks) ? def.marks : "—"} • Negative: ${Number.isFinite(def.negative) ? def.negative : "—"}`}
                >
                  {opt.label} <span className="opacity-75 ms-1">({tag})</span>
                </Badge>
              );
            })}
        </div>

        {/* Show template-level negative marking rule */}
        {tpl?.allowNegativeMarking === false ? (
          <div className="mt-2 small text-muted">
            Negative marking is <b>disabled</b> in this template; imported questions will use <b>0</b> as negative marks.
          </div>
        ) : (
          <div className="mt-2 small text-muted">
            Negative marking is <b>enabled</b>. Section defaults apply unless overridden below.
          </div>
        )}
      </div>

      {/* Overrides (placeholders now reflect what will apply) */}
      {(() => {
        // derive smart placeholders from selection + section defaults
        const single = mapSections.length === 1 ? mapSections[0] : null;
        const def = single ? (sectionDefaults.get(single) || {}) : {};
        const marksPH = single
          ? `(sec: ${Number.isFinite(def.marks) ? def.marks : "—"}; source if none)`
          : `(keep section/source)`;
        const negPH = single
          ? `(sec: ${Number.isFinite(def.negative) ? def.negative : "—"}; source/0 if none)`
          : `(keep section/source)`;
        const negDisabled = tpl?.allowNegativeMarking === false;

        return (
          <>
            <Row className="g-2">
              <Col md={6}>
                <Form.Label className="mb-1">Marks (override)</Form.Label>
                <Form.Control
                  value={ovMarks}
                  onChange={(e) => setOvMarks(e.target.value.replace(/[^0-9.]/g, ""))}
                  placeholder={marksPH}
                  inputMode="decimal"
                />
                <Form.Text muted>
                  If set, this wins over section default/source.
                </Form.Text>
              </Col>
              <Col md={6}>
                <Form.Label className="mb-1">Negative (override)</Form.Label>
                <Form.Control
                  value={ovNeg}
                  onChange={(e) => setOvNeg(e.target.value.replace(/[^-0-9.]/g, ""))}
                  placeholder={negPH}
                  inputMode="decimal"
                  disabled={negDisabled}
                />
                <Form.Text muted>
                  {negDisabled
                    ? "Template has negatives off; saved as 0."
                    : "If set, this wins over section default/source."}
                </Form.Text>
              </Col>
            </Row>
          </>
        );
      })()}

      <hr />

      <div className="d-flex align-items-center justify-content-between">
        <div className="small text-muted">Selected: <b>{selected.size}</b></div>
        <Button
          variant="outline-primary"
          disabled={selected.size === 0}
          onClick={() => setPaperOpen(true)}
          title="Preview full paper with answer key & explanations"
        >
          Preview Paper
        </Button>
        <Button disabled={!canImport || importing} onClick={importSelected}>
          {importing ? "Importing…" : "Import to Template"}
        </Button>
      </div>
    </Card.Body>
  </Card>
</Col>

      </Row>
{/* Paper Preview modal */}
<Modal show={paperOpen} onHide={()=>setPaperOpen(false)} size="xl" centered>
  <Modal.Header closeButton>
    <Modal.Title>Preview Paper</Modal.Title>
  </Modal.Header>
  <Modal.Body>
    {selected.size === 0 ? (
      <div className="text-muted">No questions selected.</div>
    ) : (
      <>
        {/* Header */}
        <div className="pb-3 mb-3 border-bottom">
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
            <div>
              <h5 className="m-0">{tpl?.name || "Question Paper"}</h5>
              <div className="text-muted small">
                {mapSubjectId ? <>Subject: <strong>{mapSubjectId}</strong> · </> : null}
                {mapSections?.length ? <>Sections: <strong>{mapSections.join(", ")}</strong> · </> : null}
                Questions: <strong>{selected.size}</strong>
              </div>
            </div>
            <div className="d-print-none">
              <Button variant="outline-secondary" onClick={() => window.print()}>
                <i className="bi bi-printer me-1" /> Print
              </Button>
            </div>
          </div>
        </div>

        {/* Paper body (questions only) */}
        <div className="paper-questions">
          {Array.from(selected.values()).map((q, idx) => (
            <div key={q.id} className="pb-3 mb-3 border-bottom">
              <div className="d-flex align-items-center gap-2 mb-2">
                <div className="fw-semibold">Q{idx+1}.</div>
                <Badge bg="light" text="dark" className="border">{q.subject || "-"}</Badge>
                <Badge bg="secondary">{q.questionType || "mcq"}</Badge>
                <Badge bg={q.difficulty === "Hard" ? "danger" : q.difficulty === "Easy" ? "success" : "secondary"}>
                  {q.difficulty || "Medium"}
                </Badge>
                {q.chapter && <Badge bg="light" text="dark" className="border">Ch: {q.chapter}</Badge>}
                {q.topic && <Badge bg="light" text="dark" className="border">{q.topic}</Badge>}
              </div>
              <QuestionBody q={q} />
            </div>
          ))}
        </div>

        {/* Answers & Explanations */}
        <div className="pt-2">
          <h6 className="mt-4">Answer Key &amp; Explanations</h6>
          <div className="small text-muted mb-2">Answers are shown here; not in the main paper.</div>
          {Array.from(selected.values()).map((q, idx) => (
            <div key={q.id} className="mb-3">
              <div className="fw-semibold">Q{idx+1}: <span className="text-success">{buildAnswerLabel(q)}</span></div>
              {!!q.explanationHtml && (
                <div className="text-muted" dangerouslySetInnerHTML={{ __html: q.explanationHtml }} />
              )}
            </div>
          ))}
        </div>
      </>
    )}
  </Modal.Body>
</Modal>

      {/* Preview modal (no "updated" text) */}
      <Modal show={!!preview} onHide={() => setPreview(null)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Preview Question</Modal.Title>
        </Modal.Header>
        <Modal.Body>
        {!preview ? null : (
     <>
       <div className="d-flex flex-wrap gap-2 mb-2">
         {getClassLabel(preview) && <Badge bg="light" text="dark" className="border">{getClassLabel(preview)}</Badge>}
         {preview.subject && <Badge bg="light" text="dark" className="border">{preview.subject}</Badge>}
         {getChapterLabel(preview) && <Badge bg="light" text="dark" className="border">Ch: {getChapterLabel(preview)}</Badge>}
         {getTopicLabel(preview) && <Badge bg="light" text="dark" className="border">{getTopicLabel(preview)}</Badge>}
         <Badge bg="info">{preview.questionType || "-"}</Badge>
         <Badge bg={preview.difficulty === "Hard" ? "danger" : preview.difficulty === "Easy" ? "success" : "secondary"}>
           {preview.difficulty || "Medium"}
         </Badge>
       </div>
       <QuestionDetails q={preview} />
     </>
   )}
        </Modal.Body>
      </Modal>

      {/* Local styles (dark/light friendly) */}
      <style>{`
        .tdb-eyebrow { font-size: 12px; letter-spacing:.08em; text-transform: uppercase; color: var(--bs-secondary-color); }

        .qb-hero {
          border: 1px solid var(--border);
          border-radius: 16px;
          background:
            radial-gradient(60% 120% at 10% -20%, color-mix(in oklab, var(--brand-accent) 16%, transparent) 0%, transparent 60%),
            radial-gradient(40% 120% at 110% 10%, color-mix(in oklab, var(--brand-accent) 10%, transparent) 0%, transparent 60%),
            color-mix(in oklab, var(--surface) 96%, transparent);
        }
        .qb-hero-inner { display:flex; align-items:center; justify-content:space-between; padding:14px 16px; border-radius:16px; }

        .qb-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
          gap: 12px;
        }
        @media (max-width: 576px){ .qb-grid { grid-template-columns: 1fr; } }

        .qb-card, .qb-skel {
          border: 1px solid var(--border);
          border-radius: 14px;
          padding: 12px;
          background: color-mix(in oklab, var(--surface) 96%, transparent);
          transition: transform .18s ease, box-shadow .18s ease;
        }
        .qb-card:hover { transform: translateY(-2px); box-shadow: 0 .8rem 1.6rem rgba(0,0,0,.08); }
        [data-bs-theme="dark"] .qb-card:hover { box-shadow: 0 .8rem 1.6rem rgba(0,0,0,.35); }
        .qb-card.is-selected { outline: 2px solid var(--brand-accent, #6a8cff); }

        .qb-html :where(img, video, iframe){ max-width: 100%; height: auto; }
        .qb-html table { width: 100%; overflow: auto; display: block; }

        .qb-sticky { position: sticky; top: 12px; }

        .selectable-badge {
          border-radius: 999px;
          cursor: pointer;
          padding: 6px 10px;
        }

        /* loading skeleton */
        .qb-skel .line{
          height: 12px; border-radius: 8px;
          background: linear-gradient(90deg, rgba(0,0,0,.08) 25%, rgba(0,0,0,.12) 37%, rgba(0,0,0,.08) 63%);
          background-size: 400% 100%; animation: shimmer 1.2s ease-in-out infinite;
        }
        [data-bs-theme="dark"] .qb-skel .line{
          background: linear-gradient(90deg, rgba(255,255,255,.08) 25%, rgba(255,255,255,.12) 37%, rgba(255,255,255,.08) 63%);
        }
        .w-50{width:50%}.w-35{width:35%}.w-90{width:90%}.w-80{width:80%}.w-70{width:70%}
        @keyframes shimmer { 0%{background-position: 100% 0} 100%{background-position: 0 0} }
      `}</style>
    </div>
  );
}
